import React, { FC, Suspense, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Drawer } from 'src/components/widgets/drawer';
import { T, backofficeUrl } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import type { addAIVirtualModelRulePanel_AssignProductMutation } from './__generated__/addAIVirtualModelRulePanel_AssignProductMutation.graphql';
import type { addAIVirtualModelRulePanel_AIVirtualModelRulesListQuery } from './__generated__/addAIVirtualModelRulePanel_AIVirtualModelRulesListQuery.graphql';
import {
    EmptyState,
    FooterButton,
    ItemActionSuccess,
    ItemDot,
    ItemName,
    ItemNameAction,
    PanelItem,
    PanelItemSpaced,
    StackedBody,
    StackedFooterButtons
} from '../common/styled';

const AddAIVirtualModelRulePanelContent: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
}> = ({ productId, productRelayId, existingRuleIds, onBack, onAssigned }) => {
    const navigate = useNavigate();
    const [assigning, setAssigning] = useState<string | null>(null);

    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addAIVirtualModelRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productRelayId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addAIVirtualModelRulePanel_AIVirtualModelRulesListQuery>(
        graphql`
            query addAIVirtualModelRulePanel_AIVirtualModelRulesListQuery {
                aiVirtualModelRules {
                    id
                    ruleID
                    name
                }
            }
        `,
        {}
    );

    const allRules = React.useMemo(() => {
        const rules = data?.aiVirtualModelRules || [];
        return rules.map((rule) => ({ id: rule!.id, name: rule!.name || rule!.id }));
    }, [data]);

    const [commitAssign] = useMutation<addAIVirtualModelRulePanel_AssignProductMutation>(graphql`
        mutation addAIVirtualModelRulePanel_AssignProductMutation($ruleId: ID!, $productIds: [Long!]!) {
            assignProductsToAIVirtualModelRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (rule: { id: string; name: string }) => {
        setAssigning(rule.id);
        commitAssign({
            variables: {
                ruleId: rule.id,
                productIds: [productId]
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allRules.filter((rule) => !existingRuleIds.includes(rule.id));

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add AI try-on rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All AI try-on rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((rule) => (
                    <PanelItem key={rule.id} $clickable={assigning === null} onClick={() => assigning === null && handleAssign(rule)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{rule.name}</ItemName>
                        <ItemActionSuccess>{assigning === rule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced
                    $clickable
                    onClick={() => {
                        onBack();
                        navigate(backofficeUrl('/ai/virtual-model-rule'));
                    }}
                >
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>+ {T._('Create new', 'Admin')}</ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddAIVirtualModelRulePanel: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
}> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add AI try-on rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <AddAIVirtualModelRulePanelContent {...props} />
        </Suspense>
    );
};
